'use client'

import { categorizeFieldType } from '@repo/validation/customDataFieldValidation'
import { CustomDataFieldAddress } from '@repo/custom-data-pages/CustomDataFieldAddress'
import { CustomDataFieldContact } from '@repo/custom-data-pages/CustomDataFieldContact'
import { CustomDataFieldDate } from '@repo/custom-data-pages/CustomDataFieldDate'
import { CustomDataFieldList } from '@repo/custom-data-pages/CustomDataFieldList'
import { CustomDataFieldOrganisation } from '@repo/custom-data-pages/CustomDataFieldOrganisation'
import { CustomDataFieldReadState } from '@repo/custom-data-pages/CustomDataFieldReadState'
import { CustomDataFieldText } from '@repo/custom-data-pages/CustomDataFieldText'
import { CustomDataSaveButtons } from '@repo/custom-data-pages/CustomDataSaveButtons'
import { useState, useEffect } from 'react'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'

export const CustomDataField = ({
  field,
  value,
  pageIsInEditMode,
  sx = {},
  onSaveInline,
  onCancelInline,
  onUpdateValue,
  valuesLoaded,
  validationErrors,
  includeBorder = true
}) => {
  const [showInlineEdit, setShowInlineEdit] = useState(false)
  const { fieldType, description, fieldId, customDataFieldId, customDataPageId } = field
  const { fieldTypeIsString, fieldTypeIsNumber, fieldTypeIsAnId } = categorizeFieldType({ fieldType })

  const saveInlineEdit = async (e) => {
    if (onSaveInline) {
      const saved = await onSaveInline(e, true, fieldId)
      if (saved) setShowInlineEdit(false)
    }
  }

  const cancelInlineEdit = async (e) => {
    if (onCancelInline) await onCancelInline(e, true, fieldId)
    setShowInlineEdit(false)
  }

  const getValueProp = () => {
    if (!fieldTypeIsNumber) return value || ''

    if (value === 0 || value === '0') return 0

    if (value) return Number(value)

    return null
  }

  const props = {
    isInlineEditMode: showInlineEdit,
    field,
    value: getValueProp(),
    onChange: (value) => onUpdateValue({ field, value }),
    fieldTypeIsString,
    fieldTypeIsNumber,
    fieldTypeIsAnId,
    validationErrors
  }

  const renderCustomDataField = () => {
    switch (fieldType) {
      case 'Date':
        return <CustomDataFieldDate {...props} />
      case 'List':
        return <CustomDataFieldList {...props} />
      case 'Address':
        return <CustomDataFieldAddress {...props} />
      case 'Contact':
        return <CustomDataFieldContact {...props} />
      case 'Org':
        return <CustomDataFieldOrganisation {...props} />
      case 'Text':
      case 'Multiline':
      case 'Email':
      case 'URL':
      case 'Integer':
      case 'Decimal':
      case 'Money':
      case 'Formula':
        return <CustomDataFieldText {...props} />
      default:
        return null
    }
  }

  useEffect(() => {
    if (pageIsInEditMode) {
      setShowInlineEdit(false)
    }
  }, [pageIsInEditMode])

  if (!field) return <MUIStack data-testid="CustomDataField" />

  const cursor = showInlineEdit || pageIsInEditMode ? 'default' : 'pointer'
  const rowId = customDataFieldId || fieldId

  return (
    <MUIStack
      data-testid="CustomDataField"
      direction="row"
      gap={6}
      justifyContent="flex-start"
      id={`custom-data-field-${customDataPageId}-${rowId}`}
      flexWrap={'wrap'}
      sx={{
        width: '100%',
        p: '20px 34px',
        borderRadius: showInlineEdit ? 1 : 0,
        cursor,
        position: 'relative',
        transition: 'all 100ms',
        borderTop: includeBorder ? (pageIsInEditMode ? '1px solid #fff' : '1px solid #eee') : 'none',
        backgroundColor: validationErrors?.length ? 'rgb(250, 235, 237)' : showInlineEdit ? '#DFD1B655' : 'transparent',
        ':hover': {
          borderRadius: 1,
          backgroundColor: validationErrors?.length
            ? 'rgb(250, 235, 237)'
            : showInlineEdit
              ? '#DFD1B655'
              : pageIsInEditMode
                ? 'transparent'
                : 'secondary.focus',
          '[data-testid="CustomDataFieldOverflowMenu"]': {
            visibility: 'visible'
          }
        },
        ...sx
      }}
    >
      <MUITypography
        variant="h6"
        sx={{
          textTransform: 'uppercase',
          cursor,
          width: '340px',
          mb: '8px !important',
          color: validationErrors?.length ? 'error.dark' : 'text.primary'
        }}
      >
        {description} {field.required ? <strong style={{ color: '#C33241', fontSize: '1rem' }}>*</strong> : ''}
      </MUITypography>

      <MUIStack
        direction="column"
        className="fieldData"
        gap={2}
        sx={{ flex: 1, ml: '0 !important', minWidth: '400px' }}
      >
        {!pageIsInEditMode && !showInlineEdit ? (
          <CustomDataFieldReadState
            valuesLoaded={valuesLoaded}
            value={value}
            field={field}
            setShowInlineEdit={setShowInlineEdit}
          />
        ) : null}

        {(pageIsInEditMode || showInlineEdit) && valuesLoaded ? (
          <>
            <MUIStack direction="column" sx={{ flex: 1, width: '100%' }} alignItems={'flex-end'} gap={2}>
              {renderCustomDataField()}
              <ValidationErrors validationErrors={validationErrors} />
              {showInlineEdit ? (
                <CustomDataSaveButtons
                  onSave={saveInlineEdit}
                  onCancel={cancelInlineEdit}
                  sx={{ width: '180px', minWidth: '180px', maxWidth: '180px', pl: 0, justifyContent: 'flex-end' }}
                />
              ) : null}
            </MUIStack>
          </>
        ) : null}
      </MUIStack>
    </MUIStack>
  )
}

CustomDataField.propTypes = {
  field: PropTypes.object,
  value: PropTypes.any,
  pageIsInEditMode: PropTypes.bool,
  onSaveInline: PropTypes.func,
  onCancelInline: PropTypes.func,
  onUpdateValue: PropTypes.func,
  sx: PropTypes.object,
  valuesLoaded: PropTypes.bool,
  validationErrors: PropTypes.array,
  includeBorder: PropTypes.bool,
  size: PropTypes.string
}

const ValidationErrors = ({ validationErrors }) => {
  if (!validationErrors?.length) return null

  return (
    <MUIStack
      direction="column"
      sx={{
        flex: 1,
        mt: 3,
        mb: 3,
        width: '100%'
      }}
      gap={2}
    >
      {validationErrors.map((error) => {
        return (
          <MUITypography
            variant="body1"
            key={error}
            sx={{
              mb: 0,
              color: 'error.dark',
              fontWeight: 500
            }}
          >
            {error}
          </MUITypography>
        )
      })}
    </MUIStack>
  )
}

ValidationErrors.propTypes = {
  validationErrors: PropTypes.array
}
